class ProductMeasurements extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      measurementsList: '[data-measurements-list]',
      measurementsJSon: '[data-product-measurements-json]',
      selector: '[data-measurements-selector]',
      selectorTrigger: '[data-measurements-selector-trigger]',
      selectorTriggerText: '[data-measurements-selector-trigger-text]',
      selectorOptions: '[data-measurements-selector-options]',
      selectorRadio: '[data-measurements-selector-radio]',
    };

    this.classes = {
      sizeSelectorActive: 'size-guide__selector--active',
    };

    this.measurementsList = this.querySelector('.size-guide__measurements-list');
    this.measurementsJsonElem = this.querySelector(this.selectors.measurementsJSon);
    this.measurements = JSON.parse(this.measurementsJsonElem ? this.measurementsJsonElem.textContent : '{}');
    this.selectorContainer = this.querySelector(this.selectors.selector);
    this.selectorTrigger = this.selectorContainer?.querySelector(this.selectors.selectorTrigger);
    this.selectorTriggerText = this.selectorContainer?.querySelector(this.selectors.selectorTriggerText);
    this.selectorOptions = this.selectorContainer?.querySelector(this.selectors.selectorOptions);
    this.sizeRadios = this.selectorContainer?.querySelectorAll(this.selectors.selectorRadio);
    this.unit = 'cm';
    this.measurementsSizes = Object.keys(this.measurements);
    this.selectedSize = this.selectorTriggerText?.textContent ?? null;

    this.init();
  }

  init() {
    this.initSizeSelectorToggle();
    this.initSizeChange();
    this.initUnitChangeListener();
    this.initVariantChangeListener();
  }

  initSizeSelectorToggle() {
    this.selectorTrigger?.addEventListener('click', () => {
      this.selectorContainer.classList.toggle(this.classes.sizeSelectorActive);

      if (!this.selectorContainer.classList.contains(this.classes.sizeSelectorActive)) {
        return;
      }

      if (!this.selectorOptions) return;

      const maxHeight = this.getBoundingClientRect().bottom - this.selectorOptions.getBoundingClientRect().top;
      this.selectorOptions.style.maxHeight = `${maxHeight}px`;
    });

    // Close on click outside
    document.addEventListener('click', (event) => {
      if (event.target.closest(this.selectors.selector)) return;
      this.selectorContainer.classList.remove(this.classes.sizeSelectorActive);
    });
  }

  initSizeChange() {
    this.sizeRadios?.forEach((sizeRadio) => {
      sizeRadio.addEventListener('change', () => {
        if (!sizeRadio.checked) return;

        if (this.selectedSize === sizeRadio.value) return;

        this.selectedSize = sizeRadio.value;

        this.updateMeasurements();

        // Update size selector trigger with selected size
        if (this.selectorTriggerText) {
          this.selectorTriggerText.textContent = sizeRadio.value;
        }

        // Close the size selector
        this.selectorContainer?.classList.remove(this.classes.sizeSelectorActive);
      });
    });
  }

  updateMeasurements() {
    if (!this.selectedSize) return;

    const sizeMeasurements = this.measurements[this.selectedSize];
    if (!sizeMeasurements) return;

    // Update measurements list
    this.measurementsList.innerHTML = Object.entries(sizeMeasurements)
      .map(
        ([key, value]) => `
        <li>
          <span>${key}</span>
          <span
            data-measurement-cm="${value.cm}"
            ${value.in ? `data-measurement-in="${value.in}"` : ''}
            data-measurement
          >${value[this.unit]}</span>
        </li>
      `
      )
      .join('');
  }

  initUnitChangeListener() {
    this.addEventListener('size-guide:unit-change', (event) => {
      if (!event.detail.unit) return;
      this.unit = event.detail.unit;

      this.updateMeasurements();
    });
  }

  initVariantChangeListener() {
    this.addEventListener('variant:change', (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;

      // Find the option that matches a measurement size
      const size = variant.options?.find((option) => this.measurementsSizes.includes(option));
      if (!size || this.selectedSize === size) return;

      this.selectedSize = size;

      this.updateMeasurements();

      // Update size selector trigger with selected size
      if (this.selectorTriggerText) {
        this.selectorTriggerText.textContent = size;
      }
    });
  }
}

export default function initProductMeasurements() {
  if (!customElements.get('product-measurements')) {
    customElements.define('product-measurements', ProductMeasurements);
  }
}
